import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollRestoration, useLocation } from 'react-router-dom';

import Sidebar from '../sidebar';
import Navbar from '../navbar';
import Footer from '../footer';

import { useAppSelector, useAppDispatch } from '@/shared/hooks/store';
import { selectIsScrollVisible, selectOnBoardingInfo } from '@/store/slices/onboarding/selectors';
import { HeaderHeightContext } from '../../../../shared/components/header-height-context';
import IntercomAccessiBe from '@/shared/components/IntercomAccessiBe';
import { Modal, Button } from '@/shared/ui';
import { CustomersController } from '@/shared/endpoints/customers';
import MobileAppSvg from '@/assets/images/dashboard/mobile-app/mobile-app.svg';
import Bell from '@/assets/images/dashboard/mobile-app/perks/bell.svg';
import FaceId from '@/assets/images/dashboard/mobile-app/perks/face-id.svg';
import Phone from '@/assets/images/dashboard/mobile-app/perks/phone.svg';
import Box from '@/assets/images/dashboard/mobile-app/perks/box.svg';
import Handle from '@/assets/images/dashboard/mobile-app/handle.svg';
import useDeviceType from '@/shared/hooks/useDeviceType';
import { useMobilePlatform } from "@/shared/hooks/useDeviceType";

import QRCode from 'react-qr-code'

import _ from 'lodash';

const desktopItems = [
    {
        img: Bell,
        title: 'Get Medication Reminders',
    },
    {
        img: FaceId,
        title: "Make Signing in Even Easier"
    },
    {
        img: Phone,
        title: 'Access MedBox \nfrom Anywhere'
    }
]

const mobileItems = [
    {
        img: Bell,
        title: 'Get Medication Reminders',
    },
    {
        img: Box,
        title: "See Real-Time Delivery Updates"
    },
    {
        img: FaceId,
        title: 'Make Signing in Even Easier'
    }
]


const Layout = ({ children, hasFooter = false, isThankYou }: { children?: ReactNode; hasFooter?: boolean, isThankYou?: boolean }) => {
    const showScroll = useAppSelector(selectIsScrollVisible);
    const [headerHeight, setHeaderHeight] = useState(0);
    const { pathname } = useLocation();
    const headerRef = useRef<HTMLDivElement>(null);
    const [downloadAppModal, setDownloadAppModal] = useState(false);
    const { deviceType } = useDeviceType();
    const onboardingData = useAppSelector(selectOnBoardingInfo);
    const dispatch = useAppDispatch();
    const isMobile = deviceType === 'mobile';

    const items = useMemo(() => {
        if (isMobile) {
            return mobileItems;
        }
        return desktopItems;
    }, [isMobile]);

    useEffect(() => {
        if (!onboardingData?.details) return;
        const { lastUserPromptedToDownloadAppTS } = onboardingData?.details || {};
        // never shown or 7 days have passed
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        if ((!lastUserPromptedToDownloadAppTS || (Date.now() - lastUserPromptedToDownloadAppTS) > sevenDaysInMs)) {
            setDownloadAppModal(true);
            const payload = _.cloneDeep(onboardingData);
            if (payload) {
                payload.details = { ...payload.details!, ...{ lastUserPromptedToDownloadAppTS: Date.now() } };
                dispatch(CustomersController.updateMetadata(payload));
            }
        }
    }, [dispatch, onboardingData]);

    const manualOpenDownloadAppModal = () => {
        setDownloadAppModal(true);
    };

    useEffect(() => {
        setTimeout(() => window.scrollTo(0, 0), 0);
    }, [pathname]);

    useEffect(() => {
        if (headerRef.current) {
            setHeaderHeight(headerRef?.current?.getBoundingClientRect()?.height || 0);
        }
    }, []);
    const platform = useMobilePlatform();

    const accessPointUi = useMemo(() => {
        const QRLink = import.meta.env.VITE_APP_QR_LINK;
        if (isMobile) {
            const isIOS = platform === "ios";
            const iosLink = import.meta.env.VITE_APP_APP_STORE_LINK;
            const androidLink = import.meta.env.VITE_APP_GOOGLE_PLAY_STORE_LINK;
            return (
                <Button
                    showEndIcon
                    variant="contained"
                    fontClassName='font-dmSansMedium font-bold text-18'
                    className='w-full mt-[32px]'
                    onClick={() => {
                        setDownloadAppModal(false)
                        window.location.href = isIOS ? iosLink : androidLink;
                    }}
                >
                    Open the App
                </Button>
            )
        }

        return (
            <div className='relative flex flex-col justify-center items-center w-full bg-peach-700 mt-[12px] rounded-lg p-[20px]'>
                <div className='relative p-[12px] w-full flex justify-center'>
                    <div className='relative bg-primary w-fit'>
                        <img
                            src={Handle}
                            alt="Handle top-left"
                            className="absolute left-[-12px] top-[-12px] w-[12px] h-[12px] rotate-0"
                        />

                        <img
                            src={Handle}
                            alt="Handle top-right"
                            className="absolute right-[-12px] top-[-12px] w-[12px] h-[12px] rotate-[90deg]"
                        />

                        <img
                            src={Handle}
                            alt="Handle bottom-right"
                            className="absolute right-[-12px] bottom-[-12px] w-[12px] h-[12px] rotate-[180deg]"
                        />

                        <img
                            src={Handle}
                            alt="Handle bottom-left"
                            className="absolute left-[-12px] bottom-[-12px] w-[12px] h-[12px] rotate-[270deg]"
                        />

                        <QRCode
                            size={115}
                            style={{ backgroundColor: 'white', padding: '8px' }}
                            value={QRLink}
                            viewBox={`0 0 256 256`}
                        />
                    </div>

                    <div className="absolute top-1/2 left-0 w-full h-1/2 bg-[radial-gradient(ellipse_at_top,rgba(2,131,111,0.2)_0%,transparent_50%)]" />
                    <div className="absolute top-1/2 left-1/2 w-[80%] h-[1px] -translate-x-1/2 bg-[linear-gradient(to_right,transparent,#02836F,transparent)]" />

                </div>
                <div className='flex flex-col items-center justify-center mt-[20px] gap-[5px]'>
                    <span className='font-atkinsonBold font-bold leading-[120%] tracking-[.4px] text-20'>Scan the QR Code</span>
                    <span className='font-atkinsonHyperlegible text-18 leading-[130%] text-center text-primary-900'>Open your phone's camera and point it at the QR code above. Once a link appears, tap it to download the app.</span>
                </div>
            </div>
        )
    }, [isMobile, platform]);

    const subText = useMemo(() => {
        if (isMobile) {
            return 'You can continue using this website in your browser, or you can open the MedBox mobile app to:'
        }
        return 'You can continue using this website as you are now, or if you have a smartphone, you can download the MedBox mobile app to:'
    }, [isMobile])

    return (
        <HeaderHeightContext.Provider value={headerHeight}>
            <ScrollRestoration />
            <div className={`flex min-h-screen flex-col ${showScroll ? 'h-screen overflow-hidden' : ''}`}>
                <div className={`fixed top-0 left-0 w-full h-[50px] z-50 ${isThankYou ? 'bg-[#faf4ef]' : 'bg-[#f5f9f7]'}`} />
                <div className="grid-row flex min-h-screen flex-col lg:grid lg:min-h-[calc(100vh_-_24px)] lg:grid-cols-[224px,1fr] lx:grid-cols-[267px,1fr]">
                    <Sidebar />
                    <div className={`relative flex flex-auto flex-col overflow-hidden ${isThankYou ? 'bg-[#faf4ef]' : 'pt-[83px] lg:pt-[78px]'}`}>
                        <Navbar headerRef={headerRef} hideTopBg manualOpenDownloadAppModal={manualOpenDownloadAppModal} />
                        <div className={`relative ${!isThankYou ? 'mx-auto w-[calc(100%_-_40px)] lg:w-[calc(100%_-_50px)] lx:w-[calc(100%_-_70px)] 3xl:w-[calc(100%_-_140px)] pb-28 md:pb-20' : 'pt-[6.3125rem] lg:pt-[6.8125rem] w-full'} flex-auto`}>
                            {children}
                        </div>
                    </div>
                </div>
                {hasFooter && <Footer />}
                <IntercomAccessiBe />
            </div>
            <Modal
                contentClassName='!py-[24px] !px-[20px] sm:!py-[32px] sm:!px-[32px] !max-w-[582px]'
                open={downloadAppModal}
                onClose={() => setDownloadAppModal(false)}
            >
                <div>
                    <div className='flex flex-col items-start sm:items-center gap-[24px] sm:gap-[20px]'>
                        <img src={MobileAppSvg} alt="Mobile App" className="w-[64px] h-[64px] sm:w-[72px] sm:h-[72px]" />
                        <div className='flex flex-col gap-[8px] text-left sm:text-center'>
                            <span className='block font-agrandir text-25 sm:text-30 sm-max:tracking-[-0.5008px] text-black'>
                                Try the MedBox Mobile App
                            </span>
                            <span className='font-atkinsonHyperlegible text-18 sm:text-20 leading-[130%] text-primary-900'>
                                {subText}
                            </span>
                        </div>
                    </div>
                    <div className='flex flex-col sm:flex-row gap-[8px] mt-[16px]'>
                        {items.map((item, index) => (
                            <div key={index} className='flex-1 flex flex-col items-start p-[12px] rounded-lg bg-peach-700 gap-[8px]'>
                                <div className='w-[32px] h-[32px] flex justify-center items-center bg-alabaster rounded-lg'>
                                    <img src={item.img} alt={item.title} className='w-[17px] h-[17px]' />
                                </div>
                                <span className='font-dmSans text-15 leading-[133%] font-medium'>{item.title}</span>
                            </div>
                        ))}
                    </div>
                    {accessPointUi}
                    <Button
                        variant="text"
                        fontClassName='font-dmSansMedium font-bold text-18 leading-[130%] tracking-[.0225rem]'
                        className={`w-full ${isMobile ? 'mt-[16px]' : 'mt-[24px]'}`}
                        onClick={() => setDownloadAppModal(false)}
                    >
                        Maybe Later
                    </Button>
                </div>
            </Modal>
        </HeaderHeightContext.Provider>
    );
};

export default Layout;
